import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Download, Plus, Repeat, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMoney, localIsoDate } from '../lib/format';
import { DownloadButton } from '../components/DownloadButton';
import { useClientOptions } from './Invoices';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
  type BadgeTone,
} from '../components/ui';

/**
 * Subscriptions (2026-10-28) — clients' subscriptions to the packages of the
 * internal projects Finance sells, billed each month or year until cancelled.
 * Each period's invoice is an ordinary invoice, issued on its first day by the
 * hourly run. The rules are the API's (modules/subscriptions); these pages show
 * what it decided.
 */

export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
export type Cycle = 'MONTHLY' | 'YEARLY';
export type DiscountType = 'NONE' | 'PERCENT' | 'FIXED';

interface Named {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
}

export interface SubscriptionRow {
  id: string;
  number: string;
  status: SubscriptionStatus;
  source: 'FINANCE' | 'API';
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  project: Named;
  package: Named & { isActive: boolean };
  externalSystem: { id: string; code: string; name: string; isActive: boolean } | null;
  externalRef: string | null;
  billingCycle: Cycle;
  currency: string;
  basePrice: number;
  discountType: DiscountType;
  discountValue: number;
  discountAmount: number;
  netPrice: number;
  startDate: string;
  nextBillingDate: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  pastDueSince: string | null;
  cancelAtPeriodEnd: boolean;
  endsOn: string | null;
  cancelledAt: string | null;
  billingError: string | null;
  billingErrorAt: string | null;
  createdAt: string;
  overdue?: { invoices: number; amount: number } | null;
}

const STATUS_TONE: Record<SubscriptionStatus, BadgeTone> = {
  ACTIVE: 'success',
  PAST_DUE: 'warning',
  CANCELLED: 'error',
};

/** Its status alone. */
export function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  const { t } = useI18n();
  return <Badge tone={STATUS_TONE[status]}>{t(`subs.status.${status}` as TranslationKey)}</Badge>;
}

/** Its status, and — while it runs — whether it has yet to start or is set to end. */
export function SubscriptionStatusBadges({
  subscription: s,
}: {
  subscription: Pick<SubscriptionRow, 'status' | 'startDate' | 'cancelAtPeriodEnd' | 'endsOn'>;
}) {
  const { t, locale } = useI18n();
  const today = localIsoDate();
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {s.status === 'ACTIVE' && s.startDate > today ? (
        <Badge tone="info">{t('subs.status.UPCOMING', { date: formatDate(s.startDate, locale) })}</Badge>
      ) : (
        <Badge tone={STATUS_TONE[s.status]}>{t(`subs.status.${s.status}` as TranslationKey)}</Badge>
      )}
      {s.status !== 'CANCELLED' && s.cancelAtPeriodEnd && s.endsOn && (
        <Badge tone="neutral">{t('subs.status.ENDING', { date: formatDate(s.endsOn, locale) })}</Badge>
      )}
    </span>
  );
}

/** "US$45.00 a month" — what each period is invoiced at. */
export function usePeriodPrice() {
  const { t, locale } = useI18n();
  return (s: { netPrice: number; currency: string; billingCycle: Cycle }) =>
    `${formatMoney(s.netPrice, s.currency, locale)} ${t(`subs.per.${s.billingCycle}` as TranslationKey)}`;
}

export function useClientName() {
  const { locale } = useI18n();
  return (client: { companyName: string; companyNameAr: string | null }) =>
    locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName;
}

export function useExternalSystemOptions(enabled = true) {
  return useQuery({
    queryKey: ['external-systems', 'options'],
    queryFn: () =>
      api.get<Array<{ id: string; code: string; name: string; isActive: boolean; projectIds: string[] }>>('/external-systems/options'),
    enabled,
  });
}

export interface SubscriptionProject extends Named {
  status: string;
  offered: boolean;
  offeredSince: string | null;
  closed: boolean;
  packages: number;
  activePackages: number;
  liveSubscriptions: number;
}

export function useSubscriptionProjects() {
  return useQuery({
    queryKey: ['subscription-projects'],
    queryFn: () => api.get<SubscriptionProject[]>('/subscription-projects'),
  });
}

export interface PackageOption {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  currency: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
}

/** The active packages of an offered project, with their list prices. */
export function usePackageOptions(projectId: string) {
  return useQuery({
    queryKey: ['subscription-packages', 'options', projectId],
    queryFn: () => api.get<PackageOption[]>('/subscription-packages/options', { projectId }),
    enabled: !!projectId,
  });
}

/** The subscriptions table: the list page's, and a client's or a project's. */
export function SubscriptionsTable({ rows, showClient = true }: { rows: SubscriptionRow[]; showClient?: boolean }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const clientName = useClientName();
  const priceOf = usePeriodPrice();

  return (
    <Table>
      <thead>
        <tr>
          <Th className="whitespace-nowrap">{t('subs.number')}</Th>
          <Th>{showClient ? t('subs.clientAndPackage') : t('subs.package')}</Th>
          <Th>{t('common.status')}</Th>
          <Th align="end">{t('subs.netPrice')}</Th>
          <Th className="whitespace-nowrap">{t('subs.nextBilling')}</Th>
          <Th>{t('subs.system')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <Tr key={s.id} onClick={() => navigate(`/subscriptions/${s.id}`)}>
            <Td className="whitespace-nowrap">
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{s.number}</span>
            </Td>
            <Td className="w-full min-w-[12rem] max-w-0">
              {showClient && (
                <span className="block truncate" title={clientName(s.client)}>
                  {clientName(s.client)}
                </span>
              )}
              <span className={showClient ? 'block truncate text-caption text-muted' : 'block truncate'}>
                {localized(s.project)} · {localized(s.package)}
              </span>
            </Td>
            <Td>
              <SubscriptionStatusBadges subscription={s} />
              {s.overdue && (
                <span className="mt-1 block text-caption text-warning-fg dark:text-warning-dark">
                  {t('subs.overdueCount', { count: s.overdue.invoices })}
                </span>
              )}
            </Td>
            <Td align="end" className="whitespace-nowrap tabular">
              {priceOf(s)}
              {s.discountType !== 'NONE' && (
                <span className="block text-caption text-muted">
                  {t('subs.before', { amount: formatMoney(s.basePrice, s.currency, locale) })}
                </span>
              )}
            </Td>
            <Td className="whitespace-nowrap tabular text-muted">
              {s.nextBillingDate ? formatDate(s.nextBillingDate, locale) : '—'}
              {s.billingError && (
                <span className="mt-1 flex items-center gap-1 text-caption text-error-fg dark:text-error-dark">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  {t('subs.filterFailing')}
                </span>
              )}
            </Td>
            <Td className="whitespace-nowrap text-muted">
              {s.externalSystem ? s.externalSystem.name : '—'}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

const csvCell = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The filters a link may open the list with — the overview's, say. */
const LINKED_FILTERS = ['status', 'clientId', 'projectId', 'packageId', 'billingCycle', 'externalSystemId', 'ending', 'failing'];

export function SubscriptionsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>(() =>
    Object.fromEntries([...params].filter(([key]) => LINKED_FILTERS.includes(key))),
  );
  const [exporting, setExporting] = useState(false);
  const clients = useClientOptions();
  const projects = useSubscriptionProjects();
  const systems = useExternalSystemOptions();
  const packages = usePackageOptions(filters.projectId ?? '');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', page, filters],
    queryFn: () => api.get<Paginated<SubscriptionRow>>('/subscriptions', { page, pageSize: 25, ...filters }),
  });

  const filter = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  /** Every row the filters hold, as a CSV the person saves. */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: SubscriptionRow[] = [];
      for (let p = 1; p <= 25; p++) {
        const chunk = await api.get<Paginated<SubscriptionRow>>('/subscriptions', { page: p, pageSize: 200, ...filters });
        rows.push(...chunk.items);
        if (p >= chunk.pageCount) break;
      }
      const header = ['number', 'status', 'client', 'product', 'package', 'cycle', 'currency', 'basePrice', 'discount', 'netPrice', 'startDate', 'nextBillingDate', 'endsOn', 'externalSystem', 'externalRef'];
      const lines = rows.map((s) =>
        [
          s.number,
          s.status,
          s.client.companyName,
          s.project.name,
          s.package.name,
          s.billingCycle,
          s.currency,
          s.basePrice.toFixed(2),
          s.discountAmount.toFixed(2),
          s.netPrice.toFixed(2),
          s.startDate,
          s.nextBillingDate,
          s.endsOn,
          s.externalSystem?.code,
          s.externalRef,
        ]
          .map(csvCell)
          .join(','),
      );
      const blob = new Blob([`﻿${[header.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscriptions-${localIsoDate()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('subs.title')}
        subtitle={t('subs.subtitle')}
        count={data?.total}
        action={
          <>
            <Button icon={<Download className="h-4 w-4" />} loading={exporting} onClick={() => void exportCsv()} disabled={!data?.total}>
              {t('subs.exportCsv')}
            </Button>
            {can(P.SUBSCRIPTIONS_MANAGE) && (
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/subscriptions/new')}>
                {t('subs.new')}
              </Button>
            )}
          </>
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => filter('q', e.target.value)}
          />
          <Select value={filters.clientId ?? ''} onChange={(e) => filter('clientId', e.target.value)} aria-label={t('subs.client')}>
            <option value="">{t('subs.client')} — {t('common.all')}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
          <Select
            value={filters.projectId ?? ''}
            onChange={(e) => {
              // A package belongs to one product: choosing another clears it.
              const { packageId: _p, ...rest } = filters;
              setFilters({ ...rest, projectId: e.target.value });
              setPage(1);
            }}
            aria-label={t('subs.product')}
          >
            <option value="">{t('subs.product')} — {t('common.all')}</option>
            {(projects.data ?? [])
              .filter((p) => p.offered || p.liveSubscriptions > 0)
              .map((p) => (
                <option key={p.id} value={p.id}>{localized(p)}</option>
              ))}
          </Select>
          <Select
            value={filters.packageId ?? ''}
            onChange={(e) => filter('packageId', e.target.value)}
            disabled={!filters.projectId}
            aria-label={t('subs.package')}
          >
            <option value="">{t('subs.package')} — {t('common.all')}</option>
            {(packages.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{localized(p)}</option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => filter('status', e.target.value)} aria-label={t('common.status')}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {(['ACTIVE', 'PAST_DUE', 'CANCELLED'] as const).map((s) => (
              <option key={s} value={s}>{t(`subs.status.${s}`)}</option>
            ))}
          </Select>
          <Select value={filters.billingCycle ?? ''} onChange={(e) => filter('billingCycle', e.target.value)} aria-label={t('subs.cycle')}>
            <option value="">{t('subs.cycle')} — {t('common.all')}</option>
            {(['MONTHLY', 'YEARLY'] as const).map((c) => (
              <option key={c} value={c}>{t(`subs.cycle.${c}`)}</option>
            ))}
          </Select>
          <Select value={filters.externalSystemId ?? ''} onChange={(e) => filter('externalSystemId', e.target.value)} aria-label={t('subs.system')}>
            <option value="">{t('subs.system')} — {t('common.all')}</option>
            {(systems.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
          <Select
            value={filters.ending ? 'ending' : filters.failing ? 'failing' : ''}
            onChange={(e) => {
              const { ending: _e, failing: _f, ...rest } = filters;
              setFilters(e.target.value ? { ...rest, [e.target.value]: 'true' } : rest);
              setPage(1);
            }}
            aria-label={t('common.filters')}
          >
            <option value="">{t('common.filters')} — {t('common.all')}</option>
            <option value="ending">{t('subs.filterEnding')}</option>
            <option value="failing">{t('subs.filterFailing')}</option>
          </Select>
          <label className="grid gap-1 text-caption text-muted">
            {t('subs.billingBy')}
            <Input type="date" value={filters.billingTo ?? ''} onChange={(e) => filter('billingTo', e.target.value)} />
          </label>
          <label className="grid gap-1 text-caption text-muted">
            {t('subs.startedFrom')}
            <Input type="date" value={filters.from ?? ''} onChange={(e) => filter('from', e.target.value)} />
          </label>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Repeat className="h-8 w-8" />} />
        ) : (
          <>
            <SubscriptionsTable rows={data.items} />
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/** A client's subscriptions — the Subscriptions tab on the client's page. */
export function ClientSubscriptionsTab({ clientId }: { clientId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscriptions', 'client', clientId],
    queryFn: () => api.get<Paginated<SubscriptionRow>>('/subscriptions', { clientId, pageSize: 200 }),
  });

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between gap-3 p-4">
        <p className="text-body-sm text-muted">{t('subs.subtitle')}</p>
        {can(P.SUBSCRIPTIONS_MANAGE) && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/subscriptions/new?clientId=${clientId}`)}>
            {t('subs.new')}
          </Button>
        )}
      </div>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.items.length ? (
        <EmptyState icon={<Repeat className="h-8 w-8" />} />
      ) : (
        <SubscriptionsTable rows={data.items} showClient={false} />
      )}
    </Card>
  );
}

/**
 * An internal project's subscriptions, on its money page: whether Finance sells
 * it, its packages and who subscribes. Nothing for a client's project.
 */
export function ProjectSubscriptionsCard({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const projects = useSubscriptionProjects();
  const product = projects.data?.find((p) => p.id === projectId);
  const { data } = useQuery({
    queryKey: ['subscriptions', 'project', projectId],
    queryFn: () => api.get<Paginated<SubscriptionRow>>('/subscriptions', { projectId, pageSize: 200 }),
    enabled: !!product,
  });
  if (!product) return null;

  return (
    <Card padded={false}>
      <CardHeader
        title={t('subs.projectCard')}
        subtitle={product.offered ? t('subs.projectCardOffered', { count: product.activePackages }) : t('pkgs.notOffered')}
        className="p-5 pb-0"
        action={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/subscription-packages?projectId=${projectId}`)}>{t('nav.subscriptionPackages')}</Button>
            {can(P.SUBSCRIPTIONS_MANAGE) && product.offered && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/subscriptions/new?projectId=${projectId}`)}>
                {t('subs.new')}
              </Button>
            )}
          </div>
        }
      />
      {data?.items.length ? (
        <SubscriptionsTable rows={data.items} />
      ) : (
        <p className="px-5 py-4 text-body-sm text-muted">{t('subs.noneForProject')}</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// One subscription
// ---------------------------------------------------------------------------

interface Person {
  id: string;
  fullName: string | null;
  fullNameAr: string | null;
}

interface DetailInvoice {
  id: string;
  number: string;
  status: string;
  periodStart: string | null;
  periodEnd: string | null;
  issueDate: string;
  dueDate: string;
  currency: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  cancelledAt: string | null;
  payments: Array<{
    id: string;
    number: string;
    amount: number;
    currency: string;
    paymentDate: string;
    paymentMethod: string;
    reference: string | null;
    reversedAt: string | null;
    externalReport: { externalTransactionId: string; system: { id: string; name: string } } | null;
  }>;
}

export interface SubscriptionDetail extends SubscriptionRow {
  notes: string | null;
  billFrom: string;
  cycleAnchor: string;
  createdBy: Person | null;
  cancelledBy: Person | null;
  cancelRequestedAt: string | null;
  cancellationReason: string | null;
  upcomingPeriod: { start: string; end: string; amount: number } | null;
  agreements: Array<{
    version: number;
    package: Named;
    billingCycle: Cycle;
    currency: string;
    basePrice: number;
    discountType: DiscountType;
    discountValue: number;
    discountAmount: number;
    netPrice: number;
    effectiveFrom: string;
    changedBy: Person | null;
    viaExternalSystem: string | null;
    reason: string | null;
    createdAt: string;
  }>;
  invoices: DetailInvoice[];
  money: { currency: string; invoiced: number; collected: number; outstanding: number };
  history: Array<{ id: string; action: string; occurredAt: string; actor: Person | null; metadata: any; newValues: any }>;
  can: { edit: boolean; cancel: boolean; resume: boolean; bill: boolean };
}

/** "10 %" or "US$5.00" — a discount as it was agreed. */
export function useDiscountText() {
  const { t, locale } = useI18n();
  return (d: { discountType: DiscountType; discountValue: number; discountAmount: number }, currency: string) =>
    d.discountType === 'NONE'
      ? t('subs.discount.NONE')
      : d.discountType === 'PERCENT'
        ? `${d.discountValue} % (${formatMoney(d.discountAmount, currency, locale)})`
        : formatMoney(d.discountAmount, currency, locale);
}

export function SubscriptionDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const clientName = useClientName();
  const priceOf = usePeriodPrice();
  const discountText = useDiscountText();

  const { data: s, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => api.get<SubscriptionDetail>(`/subscriptions/${id}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['subscription', id] });
    void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };
  const bill = useMutation({
    mutationFn: () => api.post<SubscriptionDetail & { billed: Array<{ number: string }>; billingError: string | null }>(`/subscriptions/${id}/bill`),
    onSuccess: (result) => {
      refresh();
      if (result.billingError) toast.error(result.billingError);
      else if (result.billed.length) toast.success(t('subs.detail.billed', { list: result.billed.map((b) => b.number).join(', ') }));
      else toast.success(t('subs.detail.nothingDue', { date: formatDate(result.nextBillingDate, locale) }));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const resume = useMutation({
    mutationFn: () => api.post(`/subscriptions/${id}/resume`),
    onSuccess: () => {
      refresh();
      toast.success(t('subs.detail.resumed'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });
  const reinvoice = useMutation({
    mutationFn: (start: string) => api.post(`/subscriptions/${id}/periods/${start}/reinvoice`),
    onSuccess: () => {
      refresh();
      toast.success(t('subs.detail.reinvoiced'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : t('common.error')),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !s) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const money = (n: number) => formatMoney(n, s.currency, locale);
  const manage = can(P.SUBSCRIPTIONS_MANAGE);
  const person = (p: Person | null) => (p ? localized({ fullName: p.fullName ?? undefined, fullNameAr: p.fullNameAr }) : null);
  // A period whose every invoice was cancelled is free: it can be invoiced again.
  const byPeriod = new Map<string, DetailInvoice[]>();
  for (const invoice of s.invoices) {
    const key = invoice.periodStart ?? invoice.id;
    byPeriod.set(key, [...(byPeriod.get(key) ?? []), invoice]);
  }
  const freedPeriods = new Set(
    [...byPeriod].filter(([, invoices]) => invoices.every((i) => i.status === 'CANCELLED')).map(([start]) => start),
  );

  return (
    <>
      <div className="mb-4">
        <Link to="/subscriptions" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('subs.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{s.number}</span>
            <span>{localized(s.package)}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <SubscriptionStatusBadges subscription={s} />
            <Badge tone="neutral">{t(`subs.cycle.${s.billingCycle}` as TranslationKey)}</Badge>
            <Badge tone="info">{s.currency}</Badge>
            <Link to={`/clients/${s.client.id}`}>
              <Badge tone="neutral">{s.client.code} — {clientName(s.client)}</Badge>
            </Link>
            <Link to={`/projects/${s.project.id}`}>
              <Badge tone="neutral">{s.project.code} — {localized(s.project)}</Badge>
            </Link>
          </span>
        }
        action={
          manage && (
            <div className="flex flex-wrap gap-2">
              {s.can.bill && (
                <Button loading={bill.isPending} onClick={() => bill.mutate()}>
                  {t('subs.detail.billNow')}
                </Button>
              )}
              {s.can.edit && <Button onClick={() => navigate(`/subscriptions/${id}/edit`)}>{t('subs.detail.changeTerms')}</Button>}
              {s.can.resume && (
                <Button loading={resume.isPending} onClick={() => resume.mutate()}>
                  {t('subs.detail.resume')}
                </Button>
              )}
              {s.can.cancel && (
                <Button variant="danger" onClick={() => navigate(`/subscriptions/${id}/cancel`)}>
                  {t('subs.detail.cancel')}
                </Button>
              )}
            </div>
          )
        }
      />

      {s.billingError && (
        <Card className="mb-4 border-error/40">
          <p className="text-body-sm text-error-fg dark:text-error-dark">
            {t('subs.detail.billingError', { date: formatDateTime(s.billingErrorAt, locale), error: s.billingError })}
          </p>
        </Card>
      )}
      {s.status === 'PAST_DUE' && s.pastDueSince && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('subs.detail.pastDue', { date: formatDate(s.pastDueSince, locale) })}</p>
        </Card>
      )}
      {s.status !== 'CANCELLED' && s.cancelAtPeriodEnd && s.endsOn && (
        <Card className="mb-4">
          <p className="text-body-sm">{t('subs.detail.ending', { date: formatDate(s.endsOn, locale) })}</p>
          {s.cancellationReason && <p dir="auto" className="mt-1 text-start text-body-sm text-muted">{s.cancellationReason}</p>}
        </Card>
      )}
      {s.status === 'CANCELLED' && (
        <Card className="mb-4 border-error/40">
          <p className="text-body-sm font-medium text-error-fg dark:text-error-dark">
            {t('subs.detail.cancelled', {
              date: formatDate(s.cancelledAt, locale),
              by: s.cancelledBy ? ` — ${person(s.cancelledBy)}` : '',
            })}
          </p>
          {s.cancellationReason && <p dir="auto" className="mt-1 text-start text-body-sm text-muted">{s.cancellationReason}</p>}
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="grid min-w-0 grid-cols-1 content-start gap-5 lg:col-span-2">
          <Card padded={false}>
            <CardHeader title={t('subs.detail.invoices')} subtitle={t('subs.detail.invoicesHint')} className="p-5 pb-0" />
            {s.invoices.length === 0 ? (
              <p className="px-5 py-4 text-body-sm text-muted">{s.netPrice === 0 ? t('subs.detail.free') : t('subs.detail.noInvoices')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('subs.detail.period')}</Th>
                    <Th>{t('nav.invoices')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th align="end">{t('common.total')}</Th>
                    <Th align="end">{t('subs.detail.remaining')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.invoices.map((invoice) => (
                    <InvoiceRows
                      key={invoice.id}
                      invoice={invoice}
                      reinvoice={
                        manage && invoice.periodStart && freedPeriods.has(invoice.periodStart) && invoice === byPeriod.get(invoice.periodStart)![0]
                          ? () => reinvoice.mutate(invoice.periodStart!)
                          : undefined
                      }
                      reinvoicing={reinvoice.isPending}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('subs.detail.versions')} subtitle={t('subs.detail.versionsHint')} className="p-5 pb-0" />
            <Table>
              <thead>
                <tr>
                  <Th>{t('subs.detail.versionShort')}</Th>
                  <Th>{t('subs.detail.applies')}</Th>
                  <Th>{t('subs.package')}</Th>
                  <Th align="end">{t('subs.detail.basePrice')}</Th>
                  <Th align="end">{t('subs.detail.discount')}</Th>
                  <Th align="end">{t('subs.netPrice')}</Th>
                </tr>
              </thead>
              <tbody>
                {s.agreements.map((a) => (
                  <Tr key={a.version}>
                    <Td className="tabular">{a.version}</Td>
                    <Td className="whitespace-nowrap">
                      {t('subs.detail.effectiveFrom', { date: formatDate(a.effectiveFrom, locale) })}
                      <span className="block text-caption text-muted">
                        {person(a.changedBy) ?? (a.viaExternalSystem ? t('subs.history.system') : '—')}
                        {a.reason ? ` · ${a.reason}` : ''}
                      </span>
                    </Td>
                    <Td>
                      {localized(a.package)}
                      <span className="block text-caption text-muted">{t(`subs.cycle.${a.billingCycle}` as TranslationKey)}</span>
                    </Td>
                    <Td align="end" className="tabular">{formatMoney(a.basePrice, a.currency, locale)}</Td>
                    <Td align="end" className="tabular">{discountText(a, a.currency)}</Td>
                    <Td align="end" className="tabular">{formatMoney(a.netPrice, a.currency, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card padded={false}>
            <CardHeader title={t('subs.detail.history')} className="p-5 pb-0" />
            <ul className="divide-y divide-[var(--line)] px-5 pb-3">
              {s.history.map((h) => (
                <li key={h.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
                  <span className="text-body-sm">
                    {HISTORY_KEYS.includes(h.action) ? t(`subs.history.${h.action}` as TranslationKey) : h.action}
                    <span className="ms-2 text-caption text-muted">
                      {person(h.actor) ?? (h.metadata?.via === 'API' ? t('subs.history.system') : t('subs.history.automatic'))}
                    </span>
                  </span>
                  <span className="text-caption text-muted tabular">{formatDateTime(h.occurredAt, locale)}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="grid min-w-0 grid-cols-1 content-start gap-5">
          <Card>
            <CardHeader title={t('subs.detail.agreement')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('subs.detail.net')}>
                <span className="tabular font-medium">{priceOf(s)}</span>
              </Detail>
              <Detail label={t('subs.detail.basePrice')}>{money(s.basePrice)}</Detail>
              <Detail label={t('subs.detail.discount')}>{discountText(s, s.currency)}</Detail>
              <Detail label={t('subs.detail.startDate')}>{formatDate(s.startDate, locale)}</Detail>
              <Detail label={t('subs.detail.currentPeriod')}>
                {s.currentPeriodStart ? `${formatDate(s.currentPeriodStart, locale)} – ${formatDate(s.currentPeriodEnd, locale)}` : '—'}
              </Detail>
              <Detail label={t('subs.detail.nextPeriod')}>
                {s.upcomingPeriod
                  ? t('subs.detail.nextPeriodValue', {
                      start: formatDate(s.upcomingPeriod.start, locale),
                      end: formatDate(s.upcomingPeriod.end, locale),
                    })
                  : '—'}
              </Detail>
              {s.endsOn && <Detail label={t('subs.detail.endsOn')}>{formatDate(s.endsOn, locale)}</Detail>}
              <Detail label={t('subs.detail.source')}>{t(`subs.source.${s.source}` as TranslationKey)}</Detail>
              <Detail label={t('subs.detail.createdBy')}>
                {person(s.createdBy) ?? (s.externalSystem ? s.externalSystem.name : '—')}
              </Detail>
              <Detail label={t('subs.system')}>
                {s.externalSystem ? (
                  can(P.EXTERNAL_SYSTEMS_MANAGE) ? (
                    <Link className="text-primary-600 hover:underline dark:text-primary-400" to={`/settings/external-systems/${s.externalSystem.id}`}>
                      {s.externalSystem.name}
                    </Link>
                  ) : (
                    s.externalSystem.name
                  )
                ) : (
                  '—'
                )}
              </Detail>
              {s.externalRef && (
                <Detail label={t('subs.form.externalRef')}>
                  <span className="font-mono text-mono-sm" dir="ltr">{s.externalRef}</span>
                </Detail>
              )}
            </dl>
            {s.notes && <p dir="auto" className="mt-3 whitespace-pre-wrap text-start text-body-sm text-muted">{s.notes}</p>}
            <p className="mt-3 text-caption text-faint">{t('subs.form.noTax')}</p>
          </Card>

          <Card>
            <CardHeader title={t('subs.detail.money')} subtitle={s.currency} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('subs.invoiced')}>{money(s.money.invoiced)}</Detail>
              <Detail label={t('subs.collected')}>{money(s.money.collected)}</Detail>
              <Detail label={t('subs.outstanding')}>
                <span className={s.money.outstanding > 0 ? 'font-medium text-warning-fg dark:text-warning-dark' : undefined}>
                  {money(s.money.outstanding)}
                </span>
              </Detail>
            </dl>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The history entries the dictionary names; any other shows as it was written. */
const HISTORY_KEYS = [
  'subscription.created',
  'subscription.updated',
  'subscription.terms_changed',
  'subscription.cancelled',
  'subscription.cancellation_scheduled',
  'subscription.resumed',
  'subscription.past_due',
  'subscription.back_in_good_standing',
  'subscription.billing_failed',
];

function InvoiceRows({
  invoice,
  reinvoice,
  reinvoicing,
}: {
  invoice: DetailInvoice;
  reinvoice?: () => void;
  reinvoicing: boolean;
}) {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const money = (n: number) => formatMoney(n, invoice.currency, locale);
  const cancelled = invoice.status === 'CANCELLED';
  return (
    <>
      <Tr onClick={() => navigate(`/invoices/${invoice.id}`)}>
        <Td className="whitespace-nowrap tabular">
          {invoice.periodStart ? `${formatDate(invoice.periodStart, locale)} – ${formatDate(invoice.periodEnd, locale)}` : '—'}
        </Td>
        <Td className="whitespace-nowrap">
          <span className={`font-mono text-mono-sm ${cancelled ? 'text-muted line-through' : 'text-primary-600 dark:text-primary-400'}`}>{invoice.number}</span>
          <span className="block text-caption text-muted">
            {t('subs.detail.due', { date: formatDate(invoice.dueDate, locale) })}
          </span>
        </Td>
        <Td>
          <Badge tone={statusTone(invoice.status)}>{enumLabel(invoice.status, locale)}</Badge>
          {reinvoice && (
            <Button
              size="sm"
              className="ms-2"
              icon={<RotateCcw className="h-3.5 w-3.5" />}
              loading={reinvoicing}
              onClick={(e) => {
                e.stopPropagation();
                reinvoice();
              }}
            >
              {t('subs.detail.reinvoice')}
            </Button>
          )}
        </Td>
        <Td align="end" className="tabular">{money(invoice.totalAmount)}</Td>
        <Td align="end" className="tabular">{cancelled ? '—' : money(invoice.remainingAmount)}</Td>
      </Tr>
      {invoice.payments.map((p) => (
        <Tr key={p.id}>
          <Td />
          <Td colSpan={2} className="text-caption">
            <span className={p.reversedAt ? 'text-muted line-through' : undefined}>
              {p.number} · {formatDate(p.paymentDate, locale)} · {enumLabel(p.paymentMethod, locale)}
            </span>
            {p.externalReport && (
              <span className="block text-muted">
                {t('subs.detail.reportedBy', { name: p.externalReport.system.name, transaction: p.externalReport.externalTransactionId })}
              </span>
            )}
          </Td>
          <Td align="end" className="tabular text-caption">{money(p.amount)}</Td>
          <Td align="end">
            {can(P.PAYMENTS_VIEW) && (
              <DownloadButton size="sm" path={`/payments/${p.id}/receipt`}>
                {t('subs.detail.receipt')}
              </DownloadButton>
            )}
          </Td>
        </Tr>
      ))}
    </>
  );
}
